import React, { useState, useEffect, useRef } from 'react';

import { pacientesStore, usersCollection } from '../utils/firebase';
import { createSession, updateSession }     from '../objects/session';
import { IS_PRODUCTION, URL_BUILDING, esUnidadHabilitada } from '../utils/unidadesHabilitadas';
import { obtenerNivelUsuario } from '../utils/perfiles';
import { registrarAcceso } from '../objects/user';

import macLogo from '../styles/img/logo.png';
import '../styles/global.css';

/* ─────────────────────────────────────────────────────────────
   MEJORAS APLICADAS:
   1. get() en lugar de onSnapshot → consulta puntual, sin listener permanente.
   2. where('Nick','==',nick) → solo trae el usuario que coincide,
      no descarga toda la colección.
   3. Limpieza garantizada: unsubscribe-ref por si se usa onSnapshot en el futuro;
      flag isMounted para no actualizar estado si el componente ya se desmontó.
   4. guardar() extraída del loop y convertida en función await-able correctamente.
   5. loading se resetea siempre en el bloque finally, tanto en éxito como en error.
   6. Contraseña mantenida en texto plano (según requisito).
   ───────────────────────────────────────────────────────────── */

const Login = ({
  setuserNomHospital,
  setUserCalendar,
  setUserName,
  setUserIslogged,
  setUserLevel,
  sessionList,
  setIsCorporativo,
}) => {
  const [nick,     setNick]     = useState('');
  const [pass,     setPass]     = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // flag para evitar setState si el componente se desmontó
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  /* ── Guardar sesión en Firestore + sessionStorage ──────── */
  const guardarSesion = async (usuario) => {
    // Nivel tomado de Systems[id=2] o del campo general (ver utils/perfiles.js)
    const user = { ...usuario, Permiso: obtenerNivelUsuario(usuario) };
    const token = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    const uh       = user.UnidadHospitalaria.hospital;
    const calendar = user.UnidadHospitalaria.calendar;

    // Reutilizar sesión existente de la misma UH en el sistema 2
    const existing = sessionList.find(
      (s) => s.uh === uh && (s.system === 2 || s.system === '2') && s.level !== 'calidad'
    );

    if (existing?.id) {
      await updateSession(existing.id, user.Permiso, 2, token, uh, user.id);
    } else {
      await createSession(user.Permiso, 2, token, uh, user.id);
    }

    // Último acceso a la Agenda (no bloquea el login si falla)
    registrarAcceso(user.id);

    sessionStorage.setItem('id',          user.id);
    sessionStorage.setItem('nomHospital', uh);
    sessionStorage.setItem('calendar',    calendar);
    sessionStorage.setItem('name',        user.Nombre);
    sessionStorage.setItem('level',       user.Permiso);
    sessionStorage.setItem('logged',      'true');
    sessionStorage.setItem('token',       token);

    if (!isMounted.current) return;
    setuserNomHospital(uh);
    setUserCalendar(calendar);
    setUserName(user.Nombre);
    setUserLevel(user.Permiso);
    setIsCorporativo(uh === 'Corporativo');
    setUserIslogged(true);
  };

  /* ── Handler principal de autenticación ────────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();

    // Validación básica de campos
    if (!nick.trim() || !pass.trim()) {
      setErrorMsg('Ingresa tu usuario y contraseña.');
      return;
    }

    setErrorMsg('');
    setLoading(true);

    try {
      // MEJORA 1 + 2: consulta puntual (get) filtrada por Nick
      // Solo descarga el documento del usuario que coincide
      const snap = await pacientesStore
        .collection(usersCollection)
        .where('Nick', '==', nick)
        .get();

      if (snap.empty) {
        setErrorMsg('Usuario o contraseña incorrectos.');
        return;
      }

      // Puede haber más de un doc con el mismo Nick (datos duplicados);
      // iteramos para encontrar el que también coincida con el Pass
      let usuarioValido = null;
      snap.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        // MEJORA 6: contraseña en texto plano mantenida
        if (data.Pass === pass) usuarioValido = data;
      });

      if (!usuarioValido) {
        setErrorMsg('Usuario o contraseña incorrectos.');
        return;
      }

      // Verificar que el usuario esté activo
      if (!usuarioValido.Activo) {
        setErrorMsg('Tu cuenta está desactivada. Contacta al administrador.');
        return;
      }

      // Verificar acceso al sistema 2 (Agenda Quirúrgica)
      const tieneAcceso = usuarioValido.Systems?.some(
        (s) => (s.id === 2 || s.id === '2') && s.active
      );
      if (!tieneAcceso) {
        setErrorMsg('Tu usuario no tiene acceso a este sistema.');
        return;
      }

      // En producción solo pueden entrar usuarios de unidades habilitadas
      if (IS_PRODUCTION) {
        const nomHospital = usuarioValido.UnidadHospitalaria?.hospital;
        let buildingList = [];
        try {
          const resp = await fetch(URL_BUILDING);
          buildingList = await resp.json();
        } catch (e) {
          console.warn('No se pudo cargar el catálogo de buildings:', e);
        }
        if (!esUnidadHabilitada(nomHospital, buildingList)) {
          setErrorMsg(
            `La agenda de quirófano aún no está disponible en tu unidad${nomHospital ? ` (${nomHospital})` : ''}.`
          );
          return;
        }
      }

      // MEJORA 4: guardarSesion es await-able y está fuera del loop
      await guardarSesion(usuarioValido);

    } catch (err) {
      console.error('Error en login:', err);
      if (isMounted.current) {
        setErrorMsg('Error de conexión. Intenta de nuevo.');
      }
    } finally {
      // MEJORA 5: loading siempre se resetea, en éxito y en error
      if (isMounted.current) setLoading(false);
    }
  };

  /* ── Limpiar formulario ─────────────────────────────────── */
  const handleClear = () => {
    setNick('');
    setPass('');
    setErrorMsg('');
  };

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="login-page">

      <div className="login-split">

        {/* Panel de marca */}
        <div className="login-panel--brand">
          <img src={macLogo} alt="Hospitales MAC" className="login-brand-logo" />

          <h1 className="login-brand-title">Agenda de Quirófano</h1>
          <p className="login-brand-sub">
            Coordina cada cirugía con precisión: desde la solicitud hasta el último minuto en sala.
          </p>

          {/* Firma visual: línea de electrocardiograma */}
          <svg className="login-ekg" viewBox="0 0 600 100" preserveAspectRatio="none" aria-hidden="true">
            <path
              className="login-ekg__base"
              d="M0,50 L60,50 L80,35 L100,65 L115,10 L130,85 L150,50 L300,50 L360,50 L380,35 L400,65 L415,10 L430,85 L450,50 L600,50"
            />
            <path
              className="login-ekg__pulse"
              d="M0,50 L60,50 L80,35 L100,65 L115,10 L130,85 L150,50 L300,50 L360,50 L380,35 L400,65 L415,10 L430,85 L450,50 L600,50"
            />
          </svg>

          <div className="login-foot">
            <div className="login-links">
              <a href="https://hospitalesmac.com" target="_blank" rel="noreferrer">hospitalesmac.com</a>
              <span>·</span>
              <a href="https://hospitalesmac.com/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad · Términos y condiciones</a>
            </div>
            <div className="login-meta">
              <span>© {new Date().getFullYear()} Derechos reservados HospitalesMAC</span>
              <a href="mailto:contacto@hospitalesmac.com">contacto@hospitalesmac.com</a>
              <span>versión 1.0.1</span>
            </div>
          </div>
        </div>

        {/* Panel del formulario */}
        <div className="login-panel--form">
          <div className="login-card">
            <span className="login-card__eyebrow">Acceso al sistema</span>
            <h2 className="login-card__title">Iniciar sesión</h2>

            {/* Mensaje de error */}
            {errorMsg && (
              <div className="login-card__error" role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleLogin} noValidate>

              {/* Campo: Usuario */}
              <div className="login-card__field">
                <label className="login-card__label" htmlFor="nick">
                  Usuario
                </label>
                <div className="login-card__input-wrap">
                  <input
                    id="nick"
                    className="login-card__input"
                    type="text"
                    placeholder="Ingresa tu usuario"
                    value={nick}
                    autoComplete="username"
                    autoFocus
                    disabled={loading}
                    onChange={(e) => setNick(e.target.value.replace(/\s/g, ''))}
                  />
                </div>
              </div>

              {/* Campo: Contraseña */}
              <div className="login-card__field">
                <label className="login-card__label" htmlFor="pass">
                  Contraseña
                </label>
                <div className="login-card__input-wrap">
                  <input
                    id="pass"
                    className="login-card__input"
                    type={showPass ? 'text' : 'password'}
                    placeholder="Ingresa tu contraseña"
                    value={pass}
                    autoComplete="current-password"
                    disabled={loading}
                    onChange={(e) => setPass(e.target.value.replace(/\s/g, ''))}
                  />
                  <button
                    type="button"
                    className="login-card__eye"
                    onClick={() => setShowPass((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPass ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Botones */}
              <div className="login-card__actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <span style={{
                        width: '16px', height: '16px',
                        border: '2px solid rgba(255,255,255,.35)',
                        borderTop: '2px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin .7s linear infinite',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}/>
                      Verificando...
                    </span>
                  ) : 'Ingresar'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleClear}
                  disabled={loading}
                >
                  Limpiar
                </button>
              </div>

            </form>
          </div>
        </div>

      </div>

    </div>
  );
};

export default Login;
